import {
    ColoursObj,
    Gutters,
    ImageRenderer,
    MobileConfirmDialog,
    StyledPullToRefresh,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'

import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { useThemeContext } from '../../../state/themeState/themeContext'
import { NavigationView } from '../../layout/NavigationView'
import { RequestEntry } from './components/RequestEntry'
export const AccessRequests = () => {
    const { accessRequests, acceptRequest, loadRequests, rejectRequest } =
        useBoundStore(
            useShallow((s) => {
                return {
                    accessRequests: s.accessRequests,
                    acceptRequest: s.acceptRequest,
                    loadRequests: s.loadRequests,
                    rejectRequest: s.rejectRequest,
                }
            })
        )
    const themeImages = useThemeContext((s) => s.themeImages)
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            replaceDialog: s.replaceDialog,
            clearDialog: s.clearDialog,
        }))
    )

    const onAccept = async (id: string) => {
        loadingSignal.value = true
        const resp = await acceptRequest(id)
        if (resp.status === 'error') {
            defaultAddSnack(resp.message ?? 'An unexpected error occurred', 'error')
        }
        await loadRequests()
        loadingSignal.value = false
    }

    const onReject = async (id: string) => {
        //can add a pop up box to ask for reason and then pass it to rejectRequest
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text={
                        'Are you sure you want to reject this request? This action cannot be undone.'
                    }
                    header={'Reject request'}
                    onConfirm={async () => {
                        loadingSignal.value = true
                        const resp = await rejectRequest(
                            id,
                            'No reason provided'
                        )
                        if (resp.status === 'error') {
                            defaultAddSnack(resp.message ?? 'An unexpected error occurred', 'error')
                        }
                        await loadRequests()
                        loadingSignal.value = false
                        clearDialog()
                    }}
                    onCancel={() => {
                        clearDialog()
                    }}
                />
            ),
            backdropClosable: false,
        })
    }

    return (
        <NavigationView
            navigationBarTitle={i18n.t('account.access-requests')}
            backButtonAction={() => {
                window.pushPath('/account')
            }}
            usePageTransition
        >
            <StyledPullToRefresh
                onRefresh={async () => { }}
                promiseFunction={async () => {
                    await loadRequests()
                }}
                maxPullDownDistance={100}
            >
                <Gutters style={{ height: '100%' }} topPadding="0px">
                    <Stack
                        direction={'column'}
                        alignItems={'center'}
                        gap={'16px'}
                        width={'100%'}
                    >
                        {accessRequests.length > 0 ? (
                            <Stack
                                direction={'column'}
                                alignItems={'left'}
                                gap={'16px'}
                                width={'100%'}
                            >
                                {accessRequests.map((request, index) => {
                                    return (
                                        <RequestEntry
                                            key={index}
                                            id={request?.id}
                                            status={request.status}
                                            name={`${request.firstName} ${request.lastName}`}
                                            email={request.email}
                                            message={request.message}
                                            createdAt={request.createdAt}
                                            itinId={request.itinId}
                                            itinName={request.itinName}
                                            onAccept={onAccept}
                                            onReject={onReject}
                                        />
                                    )
                                })}
                            </Stack>
                        ) : (
                            <Stack alignItems={'center'}>
                                <Typography
                                    id="noAccessRequestsSubText"
                                    variant="bodyBold"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t('account.no-access-requests')}
                                </Typography>
                                <ImageRenderer
                                    variant="Clive"
                                    src={themeImages?.Clive}
                                />
                            </Stack>
                        )}
                    </Stack>
                </Gutters>
            </StyledPullToRefresh>
        </NavigationView>
    )
}
